import * as d3 from "d3";
import { textEllipsis } from "../../shared/helpers";
import { wrappedGetComputedTextLength } from "../../shared/textLength";
import { EntityType } from "../../types/Sankey";
import { reportingColors } from "features/Library/Common/utils/constants";

type D3TextSelection = d3.Selection<SVGTextElement, any, d3.BaseType, any>;

export const lookupIconClass = (entityType: EntityType): string => {
  switch (entityType) {
    case EntityType.Video:
      return "fa-solid fa-video";
    case EntityType.EmbeddedVideo:
      return "fa-kit fa-embedded-video";
    case EntityType.Assessment:
      return "fa-solid fa-list-ol";
    case EntityType.Survey:
      return "fa-solid fa-list-ul";
    case EntityType.Email:
      return "fa-solid fa-envelope";
    case EntityType.Message:
      return "fa-solid fa-message";
    case EntityType.Event:
      return "fa-solid fa-calendar-alt";
    case EntityType.Pdf:
      return "fa-solid fa-file-alt";
    case EntityType.Link:
      return "fa-solid fa-link";
    default:
      return "fa-solid fa-archive";
  }
};

export const lookupEntityText = (entityType: EntityType) => {
  switch (entityType) {
    case EntityType.Video:
      return "video".toLocaleUpperCase();
    case EntityType.Assessment:
      return "assessment".toLocaleUpperCase();
    case EntityType.Survey:
      return "survey".toLocaleUpperCase();
    case EntityType.Email:
      return "email".toLocaleUpperCase();
    case EntityType.Message:
      return "message".toLocaleUpperCase();
    case EntityType.Event:
      return "event".toLocaleUpperCase();
    case EntityType.Pdf:
      return "pdf".toLocaleUpperCase();
    case EntityType.EmbeddedVideo:
      return "embedded video".toLocaleUpperCase();
    case EntityType.Link:
      return "link".toLocaleUpperCase();
    default:
      return "unknown".toLocaleUpperCase();
  }
};

export const lookupEntityColor = (entityType: EntityType) => {
  switch (entityType) {
    case EntityType.Video:
      return reportingColors.blue;
    case EntityType.Assessment:
      return reportingColors.yellow;
    case EntityType.Survey:
      return reportingColors.greenLight;
    case EntityType.Email:
      return reportingColors.greyLight;
    case EntityType.Message:
      return reportingColors.orange;
    case EntityType.Event:
      return reportingColors.red;
    case EntityType.Pdf:
      return reportingColors.greyDark;
    case EntityType.EmbeddedVideo:
      return reportingColors.blueLight;
    case EntityType.Link:
      return reportingColors.purple;
    default:
      return reportingColors.blue;
  }
};

export const isEntityClickable = (entityType: EntityType): boolean => {
  switch (entityType) {
    case EntityType.Video:
    case EntityType.EmbeddedVideo:
    case EntityType.Assessment:
    case EntityType.Survey:
    case EntityType.Email:
    case EntityType.Event:
    case EntityType.Pdf:
    case EntityType.Link:
      return true;
    case EntityType.Message:
    default:
      return false;
  }
};

/* istanbul ignore next */
export function wrap(textSelection: D3TextSelection, width: number, padding: number) {
  textSelection.each((_d, i, n) => {
    let text = d3.select(n[i]),
      words = text.text().split(/\s+/).reverse(),
      word: string | undefined,
      line: string[] = [],
      lineNumber = 0,
      lineHeight = 1.2, // ems
      x = text.attr("x"),
      y = text.attr("y"),
      dy = 0,
      tspan = text
        .text(null)
        .append("tspan")
        .attr("class", "titleSpan")
        .attr("x", x)
        .attr("y", y)
        .attr("dy", `${dy}em`);

    const nodeGroup = n[i].parentNode as SVGGElement;
    const rect = d3.select(nodeGroup).select("rect");
    if (rect.empty()) return;
    // 14px tall text elements at the top/bottom, +5 each for spacing
    const maxHeight = +rect.attr("height") - 38;

    while (words.length > 0) {
      word = words.pop() as string;
      line.push(word);
      tspan.text(line.join(" "));
      const node = tspan.node() as SVGTSpanElement;
      // Left/right side need padding
      if (wrappedGetComputedTextLength(node) > width - 2 * padding) {
        // Wrapping needs to occur
        if (line.length === 1) {
          // Only one line of text? Split word for wrapping
          textEllipsis(node, width, padding, [null, null, null], "");
          const amountOfLettersUsed = tspan.text().length;
          word = word.substring(amountOfLettersUsed);
        } else {
          line.pop();
          tspan.text(line.join(" "));
        }
        line = [word];
        tspan = text
          .append("tspan")
          .attr("class", "titleSpan")
          .attr("x", x)
          .attr("y", y)
          .attr("dy", `${++lineNumber * lineHeight + dy}em`)
          .text(word);
      }
      // Has the newly added line pushed us over the edge?
      if ((lineNumber + 1) * 14 * lineHeight > maxHeight) {
        // Remove the last line that is overlapping
        tspan.remove();
        // Re-add the long word
        const lastTspan = text
          .selectAll<SVGTSpanElement, unknown>("tspan")
          .filter((_spanData, index, list) => index === list.length - 1);
        lastTspan.text(`${lastTspan.text()} ${word}`);
        // Render ellipsis
        textEllipsis(lastTspan.node() as SVGTSpanElement, width, padding);
        break;
      }
    }
  });
}
